import React, { useMemo } from "react";
import styles from "../Map.module.css";

export type ItemType = "landmark" | "food";

export interface CityItem {
  id: number;
  label: string;
  type: ItemType;
}

export interface CityEntry {
  id: number;
  city: string;
  items: CityItem[];
}

interface InternalHashMapEngineProps {
  entries: CityEntry[];
  bucketCount: number;
  hashKey: (city: string) => number;
  activeCity: CityEntry | null;
}

const InternalHashMapEngine: React.FC<InternalHashMapEngineProps> = ({
  entries,
  bucketCount,
  hashKey,
  activeCity,
}) => {
  const buckets = useMemo(() => {
    const arr: { index: number; keys: string[] }[] = [];
    for (let i = 0; i < bucketCount; i++) {
      arr.push({ index: i, keys: [] });
    }
    entries.forEach((entry) => {
      const idx = hashKey(entry.city);
      arr[idx].keys.push(entry.city);
    });
    return arr;
  }, [entries, bucketCount, hashKey]);

  if (entries.length === 0) {
    return (
      <p className={styles.emptyState}>
        Buckets will populate once you generate or add some cities.
      </p>
    );
  }

  const activeBucketIndex =
    activeCity != null ? hashKey(activeCity.city) : null;

  return (
    <div className={styles.bucketGrid}>
      {buckets.map((bucket) => {
        const isHighlighted =
          activeBucketIndex !== null && bucket.index === activeBucketIndex;

        return (
          <div
            key={bucket.index}
            className={`${styles.bucketCard} ${
              isHighlighted ? styles.bucketHighlighted : ""
            }`}
          >
            <div className={styles.bucketHeader}>Bucket {bucket.index}</div>
            {bucket.keys.length === 0 ? (
              <p className={styles.emptyState}>— empty —</p>
            ) : (
              <ul className={styles.bucketList}>
                {bucket.keys.map((k) => (
                  <li key={k}>{k}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InternalHashMapEngine;
